<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { cortexKnowledgeBasesAPI } from 'dashboard/api/cortex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextCheckbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';

const props = defineProps({
  knowledgeBaseId: { type: Number, required: true },
  sources: { type: Array, default: () => [] },
});
const emit = defineEmits(['refresh', 'configureHelpCenter', 'viewDocuments']);
const { t } = useI18n();

const showForm = ref(false);
const isSaving = ref(false);
const activeAction = ref('');
const isPreviewing = ref(false);
const isReviewing = ref(false);
const preview = ref(null);
const previewSource = ref(null);
const search = ref('');
const typeFilter = ref('');
const statusFilter = ref('');
const deleteSourceDialog = ref(null);
const sourcePendingDeletion = ref(null);
const isDeletingSource = ref(false);
const referencePermissionDialog = ref(null);
const referencePermissionSource = ref(null);
const allowPublicReferences = ref(false);
const isSavingReferencePermission = ref(false);

const defaultForm = () => ({
  name: '',
  root_url: '',
  source_kind: 'page',
  canonical_locale: 'pt_BR',
  preferred_provider: 'automatic',
  max_pages: 25,
  max_depth: 2,
  include_patterns: '',
  exclude_patterns: '',
  respect_robots_txt: true,
  sync_interval: 'manual',
  missing_url_policy: 'mark_missing',
  missing_url_archive_after_runs: 3,
  review_required: true,
  allow_public_references: false,
  active: true,
  model_fallback_enabled: true,
  model_fallback_budget_usd: 0,
  youtube_audio_fallback: false,
});
const form = reactive(defaultForm());
const retryConfiguration = reactive({
  canonical_locale: 'pt_BR',
  preferred_provider: 'automatic',
});

const sourceKindOptions = computed(() => [
  { value: 'automatic', label: t('CORTEX_URLS.SOURCES.TYPES.automatic') },
  { value: 'page', label: t('CORTEX_URLS.SOURCES.TYPES.page') },
  { value: 'site', label: t('CORTEX_URLS.SOURCES.TYPES.site') },
  { value: 'sitemap', label: t('CORTEX_URLS.SOURCES.TYPES.sitemap') },
  { value: 'feed', label: t('CORTEX_URLS.SOURCES.TYPES.feed') },
  { value: 'youtube', label: t('CORTEX_URLS.SOURCES.TYPES.youtube') },
  {
    value: 'remote_file',
    label: t('CORTEX_URLS.SOURCES.TYPES.remote_file'),
  },
]);
const sourceTypeOptions = computed(() => [
  { value: 'manual', label: t('CORTEX_URLS.SOURCES.TYPES.manual') },
  {
    value: 'help_center',
    label: t('CORTEX_URLS.SOURCES.TYPES.help_center'),
  },
  { value: 'document', label: t('CORTEX_URLS.SOURCES.TYPES.document') },
  {
    value: 'structured_import',
    label: t('CORTEX_URLS.SOURCES.TYPES.structured_import'),
  },
  { value: 'crawler', label: t('CORTEX_URLS.SOURCES.TYPES.crawler') },
  { value: 'storage', label: t('CORTEX_URLS.SOURCES.TYPES.storage') },
  { value: 'api', label: t('CORTEX_URLS.SOURCES.TYPES.api') },
  {
    value: 'generated_qa',
    label: t('CORTEX_URLS.SOURCES.TYPES.generated_qa'),
  },
  {
    value: 'conversation_import',
    label: t('CORTEX_URLS.SOURCES.TYPES.conversation_import'),
  },
]);
const localeOptions = computed(() =>
  ['pt_BR', 'pt', 'en', 'es', 'de', 'fr', 'it'].map(value => ({
    value,
    label: {
      pt_BR: t(
        'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.pt_BR'
      ),
      pt: t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.pt'),
      en: t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.en'),
      es: t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.es'),
      de: t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.de'),
      fr: t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.fr'),
      it: t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.it'),
    }[value],
  }))
);
const providerOptions = computed(() => [
  { value: 'automatic', label: t('CORTEX_URLS.SOURCES.PROVIDERS.automatic') },
  { value: 'firecrawl', label: t('CORTEX_URLS.SOURCES.PROVIDERS.firecrawl') },
  { value: 'safe_http', label: t('CORTEX_URLS.SOURCES.PROVIDERS.safe_http') },
]);
const intervalOptions = computed(() => [
  { value: 'manual', label: t('CORTEX_URLS.SOURCES.INTERVALS.manual') },
  { value: 'hourly', label: t('CORTEX_URLS.SOURCES.INTERVALS.hourly') },
  { value: 'daily', label: t('CORTEX_URLS.SOURCES.INTERVALS.daily') },
  { value: 'weekly', label: t('CORTEX_URLS.SOURCES.INTERVALS.weekly') },
]);
const missingUrlPolicyOptions = computed(() => [
  {
    value: 'mark_missing',
    label: t('CORTEX_URLS.SOURCES.MISSING_URL_POLICIES.mark_missing'),
  },
  {
    value: 'archive_after_runs',
    label: t('CORTEX_URLS.SOURCES.MISSING_URL_POLICIES.archive_after_runs'),
  },
]);
const supportsMissingUrlPolicy = computed(() =>
  ['site', 'sitemap'].includes(form.source_kind)
);
const typeFilterOptions = computed(() => [
  { value: '', label: t('CORTEX_URLS.SOURCES.FILTERS.ALL_TYPES') },
  ...[...sourceKindOptions.value, ...sourceTypeOptions.value]
    .filter(option => option.value !== 'automatic')
    .filter(
      (option, index, options) =>
        options.findIndex(candidate => candidate.value === option.value) ===
        index
    ),
]);
const statusFilterOptions = computed(() => [
  { value: '', label: t('CORTEX_URLS.SOURCES.FILTERS.ALL_STATUSES') },
  { value: 'pending', label: t('CORTEX_URLS.STATUS.pending') },
  { value: 'active', label: t('CORTEX_URLS.STATUS.active') },
  { value: 'syncing', label: t('CORTEX_URLS.STATUS.syncing') },
  { value: 'failed', label: t('CORTEX_URLS.STATUS.failed') },
  { value: 'archived', label: t('CORTEX_URLS.STATUS.archived') },
]);
const sourceBooleanFields = computed(() => [
  {
    key: 'respect_robots_txt',
    label: t('CORTEX_URLS.SOURCES.FORM.RESPECT_ROBOTS_TXT'),
    help: t('CORTEX_URLS.SOURCES.FORM.HELP.RESPECT_ROBOTS_TXT'),
  },
  {
    key: 'review_required',
    label: t('CORTEX_URLS.SOURCES.FORM.REVIEW_REQUIRED'),
    help: t('CORTEX_URLS.SOURCES.FORM.HELP.REVIEW_REQUIRED'),
  },
  {
    key: 'allow_public_references',
    label: t('CORTEX_URLS.SOURCES.FORM.ALLOW_PUBLIC_REFERENCES'),
    help: t('CORTEX_URLS.SOURCES.FORM.HELP.ALLOW_PUBLIC_REFERENCES'),
  },
  {
    key: 'active',
    label: t('CORTEX_URLS.SOURCES.FORM.ACTIVE'),
    help: t('CORTEX_URLS.SOURCES.FORM.HELP.ACTIVE'),
  },
  {
    key: 'model_fallback_enabled',
    label: t('CORTEX_URLS.SOURCES.FORM.MODEL_FALLBACK_ENABLED'),
    help: t('CORTEX_URLS.SOURCES.FORM.HELP.MODEL_FALLBACK_ENABLED'),
  },
  {
    key: 'youtube_audio_fallback',
    label: t('CORTEX_URLS.SOURCES.FORM.YOUTUBE_AUDIO_FALLBACK'),
    help: t('CORTEX_URLS.SOURCES.FORM.HELP.YOUTUBE_AUDIO_FALLBACK'),
  },
]);
const sourceTableHeaders = computed(() => [
  t('CORTEX_URLS.SOURCES.TABLE.NAME'),
  t('CORTEX_URLS.SOURCES.TABLE.TYPE'),
  t('CORTEX_URLS.SOURCES.TABLE.STATUS'),
  t('CORTEX_URLS.SOURCES.TABLE.METHOD'),
  t('CORTEX_URLS.SOURCES.TABLE.FALLBACK'),
  t('CORTEX_URLS.SOURCES.TABLE.LAST_SYNC'),
  t('CORTEX_URLS.SOURCES.TABLE.NEXT_SYNC'),
  t('CORTEX_URLS.SOURCES.TABLE.ITEMS'),
  t('CORTEX_URLS.SOURCES.TABLE.FAILURES'),
  t('CORTEX_URLS.SOURCES.TABLE.ACTIONS'),
]);

const sourceKind = source =>
  source.sync_config?.source_kind || source.source_type;
const sourceKindLabel = source =>
  [...sourceKindOptions.value, ...sourceTypeOptions.value].find(
    option => option.value === sourceKind(source)
  )?.label || sourceKind(source);
const sourceStatusLabel = status =>
  statusFilterOptions.value.find(option => option.value === status)?.label ||
  status;
const sourceStatusColor = status =>
  ({
    pending: 'amber',
    active: 'teal',
    syncing: 'brand',
    failed: 'ruby',
    archived: 'slate',
  })[status] || 'slate';
const webMetadata = source => source.metadata?.web_ingestion || {};
const isWebSource = source => Boolean(source.sync_config?.root_url);
const isHelpCenterSource = source => source.source_type === 'help_center';
const translatedTools = computed(() => ({
  firecrawl: t('CORTEX_URLS.SOURCES.TOOLS.FIRECRAWL'),
  safe_http: t('CORTEX_URLS.SOURCES.TOOLS.SAFE_HTTP'),
  safe_http_download: t('CORTEX_URLS.SOURCES.TOOLS.SAFE_HTTP'),
  rss_atom: t('CORTEX_URLS.SOURCES.TOOLS.RSS_ATOM'),
  youtube_captions: t('CORTEX_URLS.SOURCES.TOOLS.YOUTUBE_CAPTIONS'),
  youtube_auto_captions: t('CORTEX_URLS.SOURCES.TOOLS.YOUTUBE_AUTO_CAPTIONS'),
  docling: t('CORTEX_URLS.SOURCES.TOOLS.DOCLING'),
  media_processor: t('CORTEX_URLS.SOURCES.TOOLS.MEDIA_PROCESSOR'),
  whisper: t('CORTEX_URLS.SOURCES.TOOLS.WHISPER'),
  paddleocr: t('CORTEX_URLS.SOURCES.TOOLS.PADDLE_OCR'),
  openai: t('CORTEX_URLS.SOURCES.TOOLS.OPENAI'),
  gemini: t('CORTEX_URLS.SOURCES.TOOLS.GEMINI'),
  direct_read: t('CORTEX_URLS.SOURCES.TOOLS.DIRECT_READ'),
}));
const toolLabel = value => {
  if (!value) return t('CORTEX_URLS.SOURCES.TOOL_PENDING');
  return translatedTools.value[value] || value;
};
const nativeMethods = computed(() => ({
  manual: t('CORTEX_URLS.SOURCES.METHODS.manual'),
  help_center: t('CORTEX_URLS.SOURCES.METHODS.help_center'),
  document: t('CORTEX_URLS.SOURCES.METHODS.document'),
  structured_import: t('CORTEX_URLS.SOURCES.METHODS.structured_import'),
  crawler: t('CORTEX_URLS.SOURCES.METHODS.crawler'),
  storage: t('CORTEX_URLS.SOURCES.METHODS.storage'),
  api: t('CORTEX_URLS.SOURCES.METHODS.api'),
  generated_qa: t('CORTEX_URLS.SOURCES.METHODS.generated_qa'),
  conversation_import: t('CORTEX_URLS.SOURCES.METHODS.conversation_import'),
}));
const sourceMethod = source => {
  if (!isWebSource(source)) {
    return (
      nativeMethods.value[source.source_type] ||
      t('CORTEX_URLS.SOURCES.NOT_AVAILABLE')
    );
  }

  return toolLabel(
    source.latest_run?.converter ||
      source.latest_run?.provider ||
      webMetadata(source).last_converter ||
      webMetadata(source).last_provider
  );
};
const firstDefined = (...values) =>
  values.find(value => value !== undefined && value !== null);
const sourceItemCount = source => {
  const value = isWebSource(source)
    ? firstDefined(
        source.latest_run?.counters?.discovered,
        webMetadata(source).counters?.discovered
      )
    : firstDefined(
        source.metadata?.synced_documents_count,
        source.metadata?.imported_documents_count,
        source.metadata?.documents_count,
        source.metadata?.items_count
      );
  return value ?? t('CORTEX_URLS.SOURCES.NOT_AVAILABLE');
};
const sourceFailureCount = source => {
  const value = isWebSource(source)
    ? firstDefined(
        source.latest_run?.counters?.failed,
        webMetadata(source).counters?.failed
      )
    : firstDefined(
        source.metadata?.failed_documents_count,
        source.metadata?.failures_count
      );
  return value ?? t('CORTEX_URLS.SOURCES.NOT_AVAILABLE');
};
const formatDate = value =>
  value
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(value))
    : t('CORTEX_URLS.SOURCES.NEVER');
const sourceNextSync = source => webMetadata(source).next_sync_at;
const sourceLastSyncLabel = source => {
  if (source.last_synced_at) return formatDate(source.last_synced_at);
  return isWebSource(source)
    ? t('CORTEX_URLS.SOURCES.NEVER')
    : t('CORTEX_URLS.SOURCES.NOT_AVAILABLE');
};
const sourceNextSyncLabel = source => {
  if (!isWebSource(source)) {
    if (source.metadata?.next_sync_at) {
      return formatDate(source.metadata.next_sync_at);
    }
    return source.source_type === 'help_center'
      ? t('CORTEX_URLS.SOURCES.MANUAL')
      : t('CORTEX_URLS.SOURCES.NOT_APPLICABLE');
  }
  if (source.sync_config?.sync_interval === 'manual') {
    return t('CORTEX_URLS.SOURCES.MANUAL');
  }
  return formatDate(sourceNextSync(source));
};
const fallbackLabel = source => {
  if (!isWebSource(source)) {
    return t('CORTEX_URLS.SOURCES.NOT_APPLICABLE');
  }

  const metadata = webMetadata(source);
  if (Object.prototype.hasOwnProperty.call(metadata, 'fallback_used')) {
    return metadata.fallback_used
      ? t('CORTEX_URLS.SOURCES.YES')
      : t('CORTEX_URLS.SOURCES.NO');
  }

  return source.status === 'syncing'
    ? t('CORTEX_URLS.SOURCES.TOOL_PENDING')
    : t('CORTEX_URLS.SOURCES.NOT_AVAILABLE');
};

const filteredSources = computed(() => {
  const term = search.value.trim().toLowerCase();
  return props.sources.filter(source => {
    const matchesTerm =
      !term ||
      source.name.toLowerCase().includes(term) ||
      source.sync_config?.root_url?.toLowerCase().includes(term);
    const matchesType =
      !typeFilter.value || sourceKind(source) === typeFilter.value;
    const matchesStatus =
      !statusFilter.value || source.status === statusFilter.value;
    return matchesTerm && matchesType && matchesStatus;
  });
});

const splitPatterns = value =>
  value
    .split('\n')
    .map(pattern => pattern.trim())
    .filter(Boolean);

const resetForm = () => Object.assign(form, defaultForm());

const createSource = async () => {
  isSaving.value = true;
  try {
    await cortexKnowledgeBasesAPI.createSource(props.knowledgeBaseId, {
      ...form,
      max_pages: Number(form.max_pages),
      max_depth: Number(form.max_depth),
      model_fallback_budget_usd: Number(form.model_fallback_budget_usd),
      missing_url_archive_after_runs: Number(
        form.missing_url_archive_after_runs
      ),
      include_patterns: splitPatterns(form.include_patterns),
      exclude_patterns: splitPatterns(form.exclude_patterns),
    });
    useAlert(t('CORTEX_URLS.SOURCES.API.CREATED'));
    resetForm();
    showForm.value = false;
    emit('refresh');
  } catch (error) {
    useAlert(t('CORTEX_URLS.SOURCES.API.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const runAction = async (source, action) => {
  const actionKey = `${source.id}:${action}`;
  activeAction.value = actionKey;
  try {
    const method = {
      sync: 'syncSource',
      pause: 'pauseSource',
      resume: 'resumeSource',
      cancel: 'cancelSource',
      retry: 'retrySource',
    }[action];
    await cortexKnowledgeBasesAPI[method](props.knowledgeBaseId, source.id);
    const successMessages = {
      sync: t('CORTEX_URLS.SOURCES.API.SYNC'),
      pause: t('CORTEX_URLS.SOURCES.API.PAUSE'),
      resume: t('CORTEX_URLS.SOURCES.API.RESUME'),
      cancel: t('CORTEX_URLS.SOURCES.API.CANCEL'),
      retry: t('CORTEX_URLS.SOURCES.API.RETRY'),
    };
    useAlert(successMessages[action]);
    emit('refresh');
  } catch (error) {
    useAlert(t('CORTEX_URLS.SOURCES.API.ERROR'));
  } finally {
    activeAction.value = '';
  }
};

const syncHelpCenterSource = async source => {
  activeAction.value = `${source.id}:sync`;
  try {
    await cortexKnowledgeBasesAPI.syncHelpCenter(
      props.knowledgeBaseId,
      source.sync_config
    );
    useAlert(t('CORTEX_URLS.SOURCES.API.SYNC'));
    emit('refresh');
  } catch (error) {
    useAlert(t('CORTEX_URLS.SOURCES.API.ERROR'));
  } finally {
    activeAction.value = '';
  }
};

const requestSourceDeletion = source => {
  sourcePendingDeletion.value = source;
  deleteSourceDialog.value?.open();
};

const configureReferencePermission = source => {
  referencePermissionSource.value = source;
  allowPublicReferences.value =
    source.sync_config?.allow_public_references === true;
  referencePermissionDialog.value?.open();
};

const saveReferencePermission = async () => {
  isSavingReferencePermission.value = true;
  try {
    await cortexKnowledgeBasesAPI.updateSource(
      props.knowledgeBaseId,
      referencePermissionSource.value.id,
      { allow_public_references: allowPublicReferences.value }
    );
    useAlert(t('CORTEX_URLS.SOURCES.REFERENCES.SAVED'));
    referencePermissionDialog.value?.close();
    emit('refresh');
  } catch (error) {
    useAlert(t('CORTEX_URLS.SOURCES.API.ERROR'));
  } finally {
    isSavingReferencePermission.value = false;
  }
};

const clearSourceDeletion = () => {
  sourcePendingDeletion.value = null;
};

const deleteSource = async () => {
  if (!sourcePendingDeletion.value) return;

  isDeletingSource.value = true;
  try {
    await cortexKnowledgeBasesAPI.deleteSource(
      props.knowledgeBaseId,
      sourcePendingDeletion.value.id
    );
    useAlert(t('CORTEX_URLS.SOURCES.API.DELETED'));
    deleteSourceDialog.value?.close();
    clearSourceDeletion();
    emit('refresh');
  } catch (error) {
    useAlert(t('CORTEX_URLS.SOURCES.API.DELETE_ERROR'));
  } finally {
    isDeletingSource.value = false;
  }
};

const openPreview = async source => {
  isPreviewing.value = true;
  previewSource.value = source;
  retryConfiguration.canonical_locale =
    source.sync_config?.canonical_locale || 'pt_BR';
  retryConfiguration.preferred_provider =
    source.sync_config?.preferred_provider || 'automatic';
  try {
    const { data } = await cortexKnowledgeBasesAPI.previewSource(
      props.knowledgeBaseId,
      source.id
    );
    preview.value = data;
  } catch (error) {
    useAlert(t('CORTEX_URLS.SOURCES.API.ERROR'));
  } finally {
    isPreviewing.value = false;
  }
};

const retryPreview = async (useFallback = false) => {
  if (!previewSource.value) return;

  const source = previewSource.value;
  const actionKey = `${source.id}:retry`;
  activeAction.value = actionKey;
  try {
    await cortexKnowledgeBasesAPI.retrySource(
      props.knowledgeBaseId,
      source.id,
      {
        ...retryConfiguration,
        ...(useFallback
          ? { preferred_provider: 'safe_http', model_fallback_enabled: true }
          : {}),
      }
    );
    useAlert(t('CORTEX_URLS.SOURCES.API.RETRY'));
    preview.value = null;
    previewSource.value = null;
    emit('refresh');
  } catch (error) {
    useAlert(t('CORTEX_URLS.SOURCES.API.ERROR'));
  } finally {
    activeAction.value = '';
  }
};

const renormalizePreview = async () => {
  if (!preview.value?.item?.id || !previewSource.value) return;

  const source = previewSource.value;
  activeAction.value = `${source.id}:renormalize`;
  try {
    await cortexKnowledgeBasesAPI.renormalizeSource(
      props.knowledgeBaseId,
      source.id,
      {
        item_id: preview.value.item.id,
        canonical_locale: retryConfiguration.canonical_locale,
      }
    );
    useAlert(t('CORTEX_URLS.SOURCES.API.RENORMALIZE'));
    preview.value = null;
    previewSource.value = null;
    emit('refresh');
  } catch (error) {
    useAlert(t('CORTEX_URLS.SOURCES.API.ERROR'));
  } finally {
    activeAction.value = '';
  }
};

const reviewPreview = async decision => {
  if (!preview.value?.item?.id || !previewSource.value) return;

  isReviewing.value = true;
  try {
    const { data } = await cortexKnowledgeBasesAPI.reviewSource(
      props.knowledgeBaseId,
      previewSource.value.id,
      { item_id: preview.value.item.id, decision }
    );
    preview.value = data;
    useAlert(
      decision === 'approve'
        ? t('CORTEX_URLS.SOURCES.REVIEW.APPROVED')
        : t('CORTEX_URLS.SOURCES.REVIEW.REJECTED')
    );
    emit('refresh');
  } catch (error) {
    useAlert(t('CORTEX_URLS.SOURCES.API.ERROR'));
  } finally {
    isReviewing.value = false;
  }
};

const closePreview = () => {
  preview.value = null;
  previewSource.value = null;
};

const attemptStatusLabel = status => {
  const labels = {
    completed: t('CORTEX_URLS.SOURCES.REVIEW.ATTEMPT_STATUS.completed'),
    failed: t('CORTEX_URLS.SOURCES.REVIEW.ATTEMPT_STATUS.failed'),
  };
  return labels[status] || status;
};
</script>

<template>
  <section class="grid min-w-0 gap-4">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CORTEX_URLS.SOURCES.TITLE') }}
        </h3>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('CORTEX_URLS.SOURCES.DESCRIPTION') }}
        </p>
      </div>
      <NextButton
        type="button"
        sm
        blue
        :icon="showForm ? 'i-lucide-x' : 'i-lucide-plus'"
        :label="
          showForm
            ? t('CORTEX_URLS.SOURCES.CLOSE_FORM')
            : t('CORTEX_URLS.SOURCES.ADD')
        "
        @click="showForm = !showForm"
      />
    </div>

    <form
      v-if="showForm"
      class="grid gap-4 rounded-lg border border-n-weak bg-n-alpha-1 p-4"
      @submit.prevent="createSource"
    >
      <div class="grid gap-4 md:grid-cols-2">
        <WithLabel
          name="cortex-url-source-name"
          :label="t('CORTEX_URLS.SOURCES.FORM.NAME')"
          :help-message="t('CORTEX_URLS.SOURCES.FORM.HELP.NAME')"
        >
          <NextInput v-model="form.name" class="w-full" required />
        </WithLabel>
        <WithLabel
          name="cortex-url-source-url"
          :label="t('CORTEX_URLS.SOURCES.FORM.URL')"
          :help-message="t('CORTEX_URLS.SOURCES.FORM.HELP.URL')"
        >
          <NextInput
            v-model="form.root_url"
            class="w-full"
            type="url"
            required
          />
        </WithLabel>
        <WithLabel
          name="cortex-url-source-type"
          :label="t('CORTEX_URLS.SOURCES.FORM.TYPE')"
          :help-message="t('CORTEX_URLS.SOURCES.FORM.HELP.TYPE')"
        >
          <AdaptiveSelect
            v-model="form.source_kind"
            :options="sourceKindOptions"
          />
        </WithLabel>
        <WithLabel
          name="cortex-url-source-locale"
          :label="t('CORTEX_URLS.SOURCES.FORM.LOCALE')"
          :help-message="t('CORTEX_URLS.SOURCES.FORM.HELP.LOCALE')"
        >
          <AdaptiveSelect
            v-model="form.canonical_locale"
            :options="localeOptions"
          />
        </WithLabel>
        <WithLabel
          name="cortex-url-source-provider"
          :label="t('CORTEX_URLS.SOURCES.FORM.PROVIDER')"
          :help-message="t('CORTEX_URLS.SOURCES.FORM.HELP.PROVIDER')"
        >
          <AdaptiveSelect
            v-model="form.preferred_provider"
            :options="providerOptions"
          />
        </WithLabel>
        <WithLabel
          name="cortex-url-source-interval"
          :label="t('CORTEX_URLS.SOURCES.FORM.INTERVAL')"
          :help-message="t('CORTEX_URLS.SOURCES.FORM.HELP.INTERVAL')"
        >
          <AdaptiveSelect
            v-model="form.sync_interval"
            :options="intervalOptions"
          />
        </WithLabel>
        <WithLabel
          name="cortex-url-source-pages"
          :label="t('CORTEX_URLS.SOURCES.FORM.MAX_PAGES')"
          :help-message="t('CORTEX_URLS.SOURCES.FORM.HELP.MAX_PAGES')"
        >
          <NextInput
            v-model="form.max_pages"
            type="number"
            min="1"
            max="500"
            class="w-full"
          />
        </WithLabel>
        <WithLabel
          name="cortex-url-source-depth"
          :label="t('CORTEX_URLS.SOURCES.FORM.MAX_DEPTH')"
          :help-message="t('CORTEX_URLS.SOURCES.FORM.HELP.MAX_DEPTH')"
        >
          <NextInput
            v-model="form.max_depth"
            type="number"
            min="0"
            max="5"
            class="w-full"
          />
        </WithLabel>
        <WithLabel
          name="cortex-url-source-include"
          :label="t('CORTEX_URLS.SOURCES.FORM.INCLUDE')"
          :help-message="t('CORTEX_URLS.SOURCES.FORM.HELP.INCLUDE')"
        >
          <TextArea
            v-model="form.include_patterns"
            :placeholder="t('CORTEX_URLS.SOURCES.FORM.PATTERNS_PLACEHOLDER')"
          />
        </WithLabel>
        <WithLabel
          name="cortex-url-source-exclude"
          :label="t('CORTEX_URLS.SOURCES.FORM.EXCLUDE')"
          :help-message="t('CORTEX_URLS.SOURCES.FORM.HELP.EXCLUDE')"
        >
          <TextArea
            v-model="form.exclude_patterns"
            :placeholder="t('CORTEX_URLS.SOURCES.FORM.PATTERNS_PLACEHOLDER')"
          />
        </WithLabel>
        <WithLabel
          name="cortex-url-source-budget"
          :label="t('CORTEX_URLS.SOURCES.FORM.BUDGET')"
          :help-message="t('CORTEX_URLS.SOURCES.FORM.HELP.BUDGET')"
        >
          <NextInput
            v-model="form.model_fallback_budget_usd"
            type="number"
            min="0"
            step="0.01"
            class="w-full"
          />
        </WithLabel>
        <WithLabel
          v-if="supportsMissingUrlPolicy"
          name="cortex-url-source-missing-policy"
          :label="t('CORTEX_URLS.SOURCES.FORM.MISSING_URL_POLICY')"
          :help-message="t('CORTEX_URLS.SOURCES.FORM.HELP.MISSING_URL_POLICY')"
        >
          <AdaptiveSelect
            v-model="form.missing_url_policy"
            :options="missingUrlPolicyOptions"
          />
        </WithLabel>
        <WithLabel
          v-if="
            supportsMissingUrlPolicy &&
            form.missing_url_policy === 'archive_after_runs'
          "
          name="cortex-url-source-missing-runs"
          :label="t('CORTEX_URLS.SOURCES.FORM.MISSING_URL_ARCHIVE_AFTER_RUNS')"
          :help-message="
            t('CORTEX_URLS.SOURCES.FORM.HELP.MISSING_URL_ARCHIVE_AFTER_RUNS')
          "
        >
          <NextInput
            v-model="form.missing_url_archive_after_runs"
            type="number"
            min="1"
            max="100"
            class="w-full"
          />
        </WithLabel>
      </div>
      <div class="grid gap-3 md:grid-cols-2">
        <label
          v-for="field in sourceBooleanFields"
          :key="field.key"
          class="flex items-start gap-3 text-sm text-n-slate-12"
        >
          <NextCheckbox v-model="form[field.key]" class="mt-0.5 shrink-0" />
          <span>
            <span class="block font-medium">
              {{ field.label }}
            </span>
            <span class="block text-label-small leading-4 text-n-slate-11">
              {{ field.help }}
            </span>
          </span>
        </label>
      </div>
      <div class="flex justify-end">
        <NextButton
          type="submit"
          blue
          icon="i-lucide-save"
          :label="t('CORTEX_URLS.SOURCES.FORM.SUBMIT')"
          :is-loading="isSaving"
        />
      </div>
    </form>

    <div class="grid gap-3 md:grid-cols-[minmax(0,1fr)_12rem_12rem]">
      <NextInput
        v-model="search"
        :placeholder="t('CORTEX_URLS.SOURCES.FILTERS.SEARCH')"
        class="w-full"
      />
      <AdaptiveSelect v-model="typeFilter" :options="typeFilterOptions" />
      <AdaptiveSelect v-model="statusFilter" :options="statusFilterOptions" />
    </div>

    <div class="overflow-x-auto rounded-lg border border-n-weak">
      <table class="min-w-full divide-y divide-n-weak text-sm">
        <thead class="bg-n-alpha-2 text-left text-xs text-n-slate-11">
          <tr>
            <th
              v-for="column in sourceTableHeaders"
              :key="column"
              class="whitespace-nowrap px-3 py-2 font-medium"
            >
              {{ column }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <template v-for="source in filteredSources" :key="source.id">
            <tr>
              <td class="max-w-64 px-3 py-2">
                <span class="block truncate font-medium text-n-slate-12">{{
                  source.name
                }}</span>
                <span class="block truncate text-xs text-n-slate-11">{{
                  source.sync_config?.root_url
                }}</span>
              </td>
              <td class="whitespace-nowrap px-3 py-2 text-n-slate-11">
                {{ sourceKindLabel(source) }}
              </td>
              <td class="whitespace-nowrap px-3 py-2">
                <Label
                  compact
                  :label="sourceStatusLabel(source.status)"
                  :color="sourceStatusColor(source.status)"
                />
              </td>
              <td class="whitespace-nowrap px-3 py-2 text-n-slate-11">
                {{ sourceMethod(source) }}
              </td>
              <td class="whitespace-nowrap px-3 py-2 text-n-slate-11">
                {{ fallbackLabel(source) }}
              </td>
              <td class="whitespace-nowrap px-3 py-2 text-n-slate-11">
                {{ sourceLastSyncLabel(source) }}
              </td>
              <td class="whitespace-nowrap px-3 py-2 text-n-slate-11">
                {{ sourceNextSyncLabel(source) }}
              </td>
              <td class="px-3 py-2 text-n-slate-11">
                {{ sourceItemCount(source) }}
              </td>
              <td class="px-3 py-2 text-n-slate-11">
                {{ sourceFailureCount(source) }}
              </td>
              <td class="px-3 py-2">
                <div class="flex items-center gap-1">
                  <NextButton
                    v-if="isWebSource(source)"
                    type="button"
                    xs
                    slate
                    ghost
                    icon="i-lucide-link"
                    :aria-label="t('CORTEX_URLS.SOURCES.REFERENCES.TITLE')"
                    :title="t('CORTEX_URLS.SOURCES.REFERENCES.TITLE')"
                    @click="configureReferencePermission(source)"
                  />
                  <NextButton
                    v-if="isWebSource(source) && source.status === 'syncing'"
                    type="button"
                    xs
                    slate
                    ghost
                    icon="i-lucide-pause"
                    :aria-label="t('CORTEX_URLS.SOURCES.ACTIONS.PAUSE')"
                    :title="t('CORTEX_URLS.SOURCES.ACTIONS.PAUSE')"
                    :is-loading="activeAction === `${source.id}:pause`"
                    @click="runAction(source, 'pause')"
                  />
                  <NextButton
                    v-if="isWebSource(source) && source.status === 'syncing'"
                    type="button"
                    xs
                    ruby
                    ghost
                    icon="i-lucide-x"
                    :aria-label="t('CORTEX_URLS.SOURCES.ACTIONS.CANCEL')"
                    :title="t('CORTEX_URLS.SOURCES.ACTIONS.CANCEL')"
                    :is-loading="activeAction === `${source.id}:cancel`"
                    @click="runAction(source, 'cancel')"
                  />
                  <NextButton
                    v-else-if="
                      isWebSource(source) &&
                      source.status === 'pending' &&
                      source.sync_config?.active === false
                    "
                    type="button"
                    xs
                    slate
                    ghost
                    icon="i-lucide-play"
                    :aria-label="t('CORTEX_URLS.SOURCES.ACTIONS.RESUME')"
                    :title="t('CORTEX_URLS.SOURCES.ACTIONS.RESUME')"
                    :is-loading="activeAction === `${source.id}:resume`"
                    @click="runAction(source, 'resume')"
                  />
                  <NextButton
                    v-else-if="
                      isWebSource(source) && source.status === 'failed'
                    "
                    type="button"
                    xs
                    amber
                    ghost
                    icon="i-lucide-rotate-cw"
                    :aria-label="t('CORTEX_URLS.SOURCES.ACTIONS.RETRY')"
                    :title="t('CORTEX_URLS.SOURCES.ACTIONS.RETRY')"
                    :is-loading="activeAction === `${source.id}:retry`"
                    @click="runAction(source, 'retry')"
                  />
                  <NextButton
                    v-else-if="
                      isWebSource(source) && source.status !== 'archived'
                    "
                    type="button"
                    xs
                    slate
                    ghost
                    icon="i-lucide-refresh-cw"
                    :aria-label="t('CORTEX_URLS.SOURCES.ACTIONS.SYNC')"
                    :title="t('CORTEX_URLS.SOURCES.ACTIONS.SYNC')"
                    :is-loading="activeAction === `${source.id}:sync`"
                    @click="runAction(source, 'sync')"
                  />
                  <NextButton
                    v-if="isHelpCenterSource(source)"
                    type="button"
                    xs
                    slate
                    ghost
                    icon="i-lucide-refresh-cw"
                    :aria-label="t('CORTEX_URLS.SOURCES.ACTIONS.SYNC')"
                    :title="t('CORTEX_URLS.SOURCES.ACTIONS.SYNC')"
                    :is-loading="activeAction === `${source.id}:sync`"
                    @click="syncHelpCenterSource(source)"
                  />
                  <NextButton
                    v-if="isHelpCenterSource(source)"
                    type="button"
                    xs
                    slate
                    ghost
                    icon="i-lucide-pencil"
                    :aria-label="t('CORTEX_URLS.SOURCES.ACTIONS.CONFIGURE')"
                    :title="t('CORTEX_URLS.SOURCES.ACTIONS.CONFIGURE')"
                    @click="emit('configureHelpCenter', source)"
                  />
                  <NextButton
                    type="button"
                    xs
                    slate
                    ghost
                    icon="i-lucide-eye"
                    :aria-label="
                      t('CORTEX_URLS.SOURCES.ACTIONS.VIEW_DOCUMENTS')
                    "
                    :title="t('CORTEX_URLS.SOURCES.ACTIONS.VIEW_DOCUMENTS')"
                    @click="emit('viewDocuments', source)"
                  />
                  <NextButton
                    v-if="
                      isWebSource(source) &&
                      source.latest_run?.status === 'awaiting_review'
                    "
                    type="button"
                    xs
                    slate
                    ghost
                    icon="i-lucide-scan-text"
                    :aria-label="t('CORTEX_URLS.SOURCES.ACTIONS.REVIEW')"
                    :title="t('CORTEX_URLS.SOURCES.ACTIONS.REVIEW')"
                    :is-loading="
                      isPreviewing && previewSource?.id === source.id
                    "
                    @click="openPreview(source)"
                  />
                  <NextButton
                    type="button"
                    xs
                    ruby
                    ghost
                    icon="i-lucide-trash-2"
                    :aria-label="t('CORTEX_URLS.SOURCES.ACTIONS.DELETE')"
                    :title="t('CORTEX_URLS.SOURCES.ACTIONS.DELETE')"
                    @click="requestSourceDeletion(source)"
                  />
                </div>
              </td>
            </tr>
            <tr v-if="preview && previewSource?.id === source.id">
              <td colspan="10" class="bg-n-alpha-1 p-4">
                <section class="grid min-w-0 gap-4">
                  <div class="flex flex-wrap items-start justify-between gap-3">
                    <div class="min-w-0">
                      <h4 class="text-sm font-semibold text-n-slate-12">
                        {{ t('CORTEX_URLS.SOURCES.REVIEW.TITLE') }}
                      </h4>
                      <p class="mt-1 truncate text-xs text-n-slate-11">
                        {{ preview.item?.canonical_url }}
                      </p>
                    </div>
                    <div class="flex items-center gap-2">
                      <a
                        :href="preview.item?.canonical_url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-flex items-center gap-1 text-xs font-medium text-n-brand hover:underline"
                      >
                        <span class="i-lucide-external-link size-3.5" />
                        {{ t('CORTEX_URLS.SOURCES.REVIEW.OPEN_ORIGINAL') }}
                      </a>
                      <NextButton
                        type="button"
                        xs
                        slate
                        ghost
                        icon="i-lucide-x"
                        :label="t('CORTEX_URLS.SOURCES.REVIEW.CLOSE')"
                        @click="closePreview"
                      />
                    </div>
                  </div>

                  <dl
                    class="grid gap-3 rounded-md border border-n-weak p-3 text-xs sm:grid-cols-2 lg:grid-cols-4"
                  >
                    <div>
                      <dt class="text-n-slate-10">
                        {{ t('CORTEX_URLS.SOURCES.REVIEW.TOOL') }}
                      </dt>
                      <dd class="mt-1 font-medium text-n-slate-12">
                        {{
                          toolLabel(
                            preview.item?.converter || preview.item?.provider
                          )
                        }}
                      </dd>
                    </div>
                    <div>
                      <dt class="text-n-slate-10">
                        {{ t('CORTEX_URLS.SOURCES.REVIEW.MODEL') }}
                      </dt>
                      <dd class="mt-1 font-medium text-n-slate-12">
                        {{
                          preview.interpretation?.classifier_model ||
                          preview.item?.extraction_metadata?.metadata?.model ||
                          t('CORTEX_URLS.SOURCES.REVIEW.NOT_USED')
                        }}
                      </dd>
                    </div>
                    <div>
                      <dt class="text-n-slate-10">
                        {{ t('CORTEX_URLS.SOURCES.REVIEW.FALLBACK') }}
                      </dt>
                      <dd class="mt-1 font-medium text-n-slate-12">
                        {{
                          preview.item?.fallback_used
                            ? t('CORTEX_URLS.SOURCES.YES')
                            : t('CORTEX_URLS.SOURCES.NO')
                        }}
                      </dd>
                    </div>
                    <div>
                      <dt class="text-n-slate-10">
                        {{ t('CORTEX_URLS.SOURCES.REVIEW.CLASSIFICATION') }}
                      </dt>
                      <dd class="mt-1 font-medium text-n-slate-12">
                        {{
                          preview.interpretation?.selected_profile ||
                          preview.interpretation?.detected_profile ||
                          t('CORTEX_URLS.SOURCES.REVIEW.NOT_AVAILABLE')
                        }}
                      </dd>
                    </div>
                  </dl>

                  <div class="grid min-w-0 gap-3 lg:grid-cols-2">
                    <div class="grid min-w-0 gap-2">
                      <h5 class="text-xs font-medium text-n-slate-11">
                        {{ t('CORTEX_URLS.SOURCES.REVIEW.EXTRACTED') }}
                      </h5>
                      <div
                        class="max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-md bg-n-slate-1 p-3 text-xs text-n-slate-12"
                      >
                        {{ preview.extraction?.raw_content }}
                      </div>
                    </div>
                    <div class="grid min-w-0 gap-2">
                      <h5 class="text-xs font-medium text-n-slate-11">
                        {{ t('CORTEX_URLS.SOURCES.REVIEW.NORMALIZED') }}
                      </h5>
                      <div
                        class="max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-md bg-n-slate-1 p-3 text-xs text-n-slate-12"
                      >
                        {{
                          preview.interpretation?.canonical_text ||
                          t('CORTEX_URLS.SOURCES.REVIEW.NOT_AVAILABLE')
                        }}
                      </div>
                    </div>
                  </div>

                  <div v-if="preview.item?.attempts?.length" class="grid gap-2">
                    <h5 class="text-xs font-medium text-n-slate-11">
                      {{ t('CORTEX_URLS.SOURCES.REVIEW.ATTEMPTS') }}
                    </h5>
                    <ul class="grid gap-1 text-xs text-n-slate-11">
                      <li
                        v-for="(attempt, index) in preview.item.attempts"
                        :key="`${attempt.processor}:${index}`"
                        class="flex flex-wrap items-center gap-x-2 rounded-md bg-n-alpha-2 px-3 py-2"
                      >
                        <span class="font-medium text-n-slate-12">
                          {{ toolLabel(attempt.processor) }}
                        </span>
                        <span>
                          {{ attemptStatusLabel(attempt.status) }}
                        </span>
                        <span v-if="attempt.duration_ms">
                          {{
                            t('CORTEX_URLS.SOURCES.REVIEW.DURATION_MS', {
                              value: attempt.duration_ms,
                            })
                          }}
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div
                    class="grid gap-3 border-t border-n-weak pt-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]"
                  >
                    <WithLabel
                      name="cortex-url-review-locale"
                      :label="t('CORTEX_URLS.SOURCES.REVIEW.LOCALE')"
                      :help-message="
                        t('CORTEX_URLS.SOURCES.REVIEW.LOCALE_HELP')
                      "
                    >
                      <AdaptiveSelect
                        v-model="retryConfiguration.canonical_locale"
                        :options="localeOptions"
                      />
                    </WithLabel>
                    <WithLabel
                      name="cortex-url-review-provider"
                      :label="t('CORTEX_URLS.SOURCES.REVIEW.PROVIDER')"
                      :help-message="
                        t('CORTEX_URLS.SOURCES.REVIEW.PROVIDER_HELP')
                      "
                    >
                      <AdaptiveSelect
                        v-model="retryConfiguration.preferred_provider"
                        :options="providerOptions"
                      />
                    </WithLabel>
                    <div class="flex flex-wrap justify-end gap-2">
                      <NextButton
                        type="button"
                        sm
                        slate
                        outline
                        icon="i-lucide-languages"
                        :label="t('CORTEX_URLS.SOURCES.REVIEW.RENORMALIZE')"
                        :is-loading="
                          activeAction === `${source.id}:renormalize`
                        "
                        @click="renormalizePreview"
                      />
                      <NextButton
                        type="button"
                        sm
                        slate
                        outline
                        icon="i-lucide-refresh-cw"
                        :label="t('CORTEX_URLS.SOURCES.REVIEW.REEXTRACT')"
                        :is-loading="activeAction === `${source.id}:retry`"
                        @click="retryPreview(false)"
                      />
                      <NextButton
                        type="button"
                        sm
                        amber
                        outline
                        icon="i-lucide-git-branch"
                        :label="t('CORTEX_URLS.SOURCES.REVIEW.TRY_FALLBACK')"
                        :is-loading="activeAction === `${source.id}:retry`"
                        @click="retryPreview(true)"
                      />
                    </div>
                  </div>

                  <div
                    v-if="preview.interpretation?.review_status === 'pending'"
                    class="flex justify-end gap-2"
                  >
                    <NextButton
                      type="button"
                      sm
                      ruby
                      outline
                      icon="i-lucide-x"
                      :label="t('CORTEX_URLS.SOURCES.REVIEW.REJECT')"
                      :is-loading="isReviewing"
                      @click="reviewPreview('reject')"
                    />
                    <NextButton
                      type="button"
                      sm
                      blue
                      icon="i-lucide-check"
                      :label="t('CORTEX_URLS.SOURCES.REVIEW.APPROVE')"
                      :is-loading="isReviewing"
                      @click="reviewPreview('approve')"
                    />
                  </div>
                </section>
              </td>
            </tr>
          </template>
          <tr v-if="!filteredSources.length">
            <td
              colspan="10"
              class="px-3 py-4 text-center text-sm text-n-slate-11"
            >
              {{ t('CORTEX_URLS.SOURCES.EMPTY') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <Dialog
      ref="referencePermissionDialog"
      :title="t('CORTEX_URLS.SOURCES.REFERENCES.TITLE')"
      :description="t('CORTEX_URLS.SOURCES.FORM.HELP.ALLOW_PUBLIC_REFERENCES')"
      :confirm-button-label="t('CORTEX_URLS.SOURCES.REFERENCES.SAVE')"
      confirm-button-icon="i-lucide-save"
      cancel-button-icon="i-lucide-x"
      :is-loading="isSavingReferencePermission"
      :disable-confirm-button="isSavingReferencePermission"
      @confirm="saveReferencePermission"
    >
      <label class="flex items-start gap-3 text-sm text-n-slate-12">
        <NextCheckbox v-model="allowPublicReferences" class="mt-0.5 shrink-0" />
        <span>{{ t('CORTEX_URLS.SOURCES.FORM.ALLOW_PUBLIC_REFERENCES') }}</span>
      </label>
    </Dialog>
    <Dialog
      ref="deleteSourceDialog"
      type="alert"
      :title="t('CORTEX_URLS.SOURCES.DELETE.TITLE')"
      :description="
        t('CORTEX_URLS.SOURCES.DELETE.DESCRIPTION', {
          name: sourcePendingDeletion?.name,
        })
      "
      :confirm-button-label="t('CORTEX_URLS.SOURCES.DELETE.CONFIRM')"
      confirm-button-icon="i-lucide-trash-2"
      :is-loading="isDeletingSource"
      @confirm="deleteSource"
      @close="clearSourceDeletion"
    />
  </section>
</template>
